"use client";
import { useCurrency } from "@/app/providers/currencyContext";
import { ShoppingBagIcon } from "@/components/_icons/shoppingBagIcon";
import { PrimaryButton } from "@/components/button/primaryButton";
import { TextButton } from "@/components/button/textButton";
import { RegularModal } from "@/components/modals/modals";
import validateTableId from "@/dataLayer/validateTableId";
import { Merchant } from "@/dto/merchant";
import { ScanToOrderInfo } from "@/dto/scanToOrderInfo";
import { ScanToOrderTableItem } from "@/dto/scanToOrderTableItem";
import { cloverColorTheme } from "@/utils/cloverColorTheme";
import { lightenColor } from "@/utils/lightenColor";
import { getFromLocalStorage } from "@/utils/localStorageUtils";
import { modifierDisplay } from "@/utils/modifierDisplayUtil";
import {
  goToGuestCheckout,
  handleS2OTableData,
} from "@/utils/scanToOrderUtils";
import { LocalStorageKey } from "@/utils/storageKeys";
import { useTranslations } from "next-intl";
import { useRouter } from "next13-progressbar";
import { useId } from "react";

export const ScanToOrderKitchenModal = ({
  isOpen,
  onRequestClose,
  merchant,
  scanToOrderInfo,
}: {
  isOpen: boolean;
  onRequestClose: () => void;
  merchant: Merchant;
  scanToOrderInfo?: ScanToOrderInfo;
}) => {
  const router = useRouter();
  const t = useTranslations("ScanToOrderCartContent");
  const merchantColor =
    merchant?.merchantMeta?.colorPalette?.color ||
    cloverColorTheme.Main.primaryColor;
  const tableData = getFromLocalStorage<any>(LocalStorageKey.TABLE_DATA, {});
  const tableItems = tableData?.order?.cart?.items as ScanToOrderTableItem[];
  const titleId = useId();
  const subtitleId = useId();

  const { format } = useCurrency();

  const onCloseOutAndPay = async () => {
    const newTableData = await validateTableId(
      merchant.id,
      scanToOrderInfo?.tableId as string
    );
    handleS2OTableData(newTableData, "invalidTable");
    goToGuestCheckout(router, scanToOrderInfo);
  };

  return (
    <RegularModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      overlayClassName="bg-opacity-75"
      contentClassName="bg-white sm:rounded-lg shadow-lg w-full h-full sm:h-[auto] sm:w-[60%] mx-auto overflow-y-auto sm:max-w-[670px] sm:max-h-[90vh]"
      aria={{ labelledby: titleId, describedby: subtitleId }}
    >
      <div className="flex flex-col items-center justify-center pt-8 pb-4 px-4 mt-16 sm:mt-0">
        <ShoppingBagIcon brandingColor={merchantColor} />
        <h2
          id={titleId}
          aria-describedby={subtitleId}
          className="font-medium text-[32px] text-[#373737] mt-4"
        >
          {t("successConfirmationTitle")}
        </h2>
        <p
          id={subtitleId}
          className="font-normal text-[15px] text-[#373737] mt-2 mb-2 text-center"
        >
          {t("successConfirmationMessage")}
        </p>
        {tableItems?.length > 0 && (
          <div className="w-full pb-32 sm:pb-0">
            {tableItems.map((item) => (
              <div
                key={item.id}
                className="flex flex-col justify-between items-center pt-4 pb-8 border-b"
              >
                <div className="w-full flex flex-row justify-between">
                  <div className="w-full flex flex-col justify-between">
                    <p className="text-lg text-[#373737] max-w-[310px]">
                      {item?.name}&nbsp;
                      {item?.quantity > 1 && `× ` + item?.quantity}
                    </p>
                    <div className="max-w-[80%]">
                      {item?.modifiers.length > 0 && (
                        <p className="text-[15px] text-[#727272]">
                          {modifierDisplay(item?.modifiers)}
                        </p>
                      )}
                      <div className="w-full">
                        <p className="text-[15px] text-[#727272] max-w-[310px] break-words">
                          {item?.specialInstructions
                            ? `"${item?.specialInstructions}"`
                            : ""}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col justify-start items-end">
                    <p className="text-[14px] font-medium mt-0.5">
                      {format(item?.price)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="fixed sm:relative bottom-0 left-0 w-full sm:mt-4 flex flex-col-reverse sm:flex-col sm:gap-4 items-center">
          <PrimaryButton
            style={{
              backgroundColor: merchantColor,
            }}
            onClick={onRequestClose}
            className="w-full text-white sm:w-[320px] h-[60px] rounded-none"
          >
            <p className="font-medium text-[20px]">{t("buttonAddMoreText")}</p>
          </PrimaryButton>
          <TextButton
            className="w-full sm:w-[320px] h-[60px] rounded-none text-[#373737]"
            onClick={onCloseOutAndPay}
            style={{
              color: "#373737",
              backgroundColor: lightenColor(merchantColor, 90),
            }}
          >
            <p className="font-medium text-[20px]">{t("buttonCloseOutText")}</p>
          </TextButton>
        </div>
      </div>
    </RegularModal>
  );
};
